import { Link } from 'react-router-dom';
import { useState } from 'react';
import { audiovisualesData } from '@/mocks/derechos-data';

export default function AudiovisualesModule() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('phone_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      setTimeout(() => setFormStatus('idle'), 4000);
      return;
    }

    setFormStatus('loading');
    setFormError('');

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'phone_alt') {
          params.append(key, value as string);
        }
      });

      const res = await fetch('https://readdy.ai/api/form/d9qcofknlsngrm4llvg0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 4000);
      } else {
        const msg = parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || responseText || 'Error al enviar el formulario';
        if (msg.toLowerCase().includes('spam') || msg.toLowerCase().includes('form data is spam')) {
          setFormError('No se pudo procesar la solicitud. Intente nuevamente.');
        } else {
          setFormError(msg);
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('Error de conexión. Intente nuevamente.');
      setFormStatus('error');
    }
  };

  return (
    <section id="audiovisuales" className="py-16 md:py-24 bg-background-100">
      {/* Intro Section */}
      <div className="px-4 md:px-6 lg:px-10 mb-16 md:mb-20">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            {/* Image */}
            <div className="relative order-2 lg:order-1">
              <div className="rounded-2xl overflow-hidden h-[300px] md:h-[420px]">
                <img
                  src={audiovisualesData.introduccion.imagen}
                  alt="Músicos grabando banda sonora"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-4 -left-4 w-24 h-24 rounded-full bg-accent-500/10 border border-accent-500/20 hidden lg:block" />
            </div>

            {/* Text */}
            <div className="order-1 lg:order-2">
              <span className="inline-block text-xs font-semibold uppercase tracking-widest text-accent-600 mb-3">
                Módulo Audiovisual
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 leading-tight mb-2">
                {audiovisualesData.introduccion.titulo}
              </h2>
              <p className="text-foreground-500 text-xs font-medium tracking-wide uppercase mb-5">
                {audiovisualesData.introduccion.subtitulo}
              </p>
              <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-3">
                {audiovisualesData.introduccion.parrafo1}
              </p>
              {/* Important clarification */}
              <div className="bg-white border border-secondary-200/60 rounded-xl p-4 mt-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-information-line text-accent-600 text-base" />
                  </div>
                  <p className="text-foreground-700 text-sm leading-relaxed">
                    <strong className="text-foreground-900">Importante:</strong> {audiovisualesData.introduccion.parrafo2}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Fijación e Incorporación */}
      <div className="px-4 md:px-6 lg:px-10 mb-16 md:mb-20">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
            {/* Fijación */}
            <div className="bg-white border border-secondary-200/60 rounded-2xl p-6 md:p-8 hover:border-accent-200/50 transition-all duration-300">
              <div className="w-10 h-10 rounded-xl bg-accent-50 flex items-center justify-center mb-4">
                <i className="ri-record-circle-line text-accent-600 text-lg" />
              </div>
              <h4 className="font-heading text-xl font-bold text-foreground-900 mb-3">
                {audiovisualesData.fijacion.titulo}
              </h4>
              <p className="text-foreground-600 text-sm leading-relaxed">
                {audiovisualesData.fijacion.descripcion}
              </p>
            </div>

            {/* Incorporación */}
            <div className="bg-white border border-secondary-200/60 rounded-2xl p-6 md:p-8 hover:border-accent-200/50 transition-all duration-300">
              <div className="w-10 h-10 rounded-xl bg-accent-50 flex items-center justify-center mb-4">
                <i className="ri-links-line text-accent-600 text-lg" />
              </div>
              <h4 className="font-heading text-xl font-bold text-foreground-900 mb-3">
                {audiovisualesData.incorporacion.titulo}
              </h4>
              <p className="text-foreground-600 text-sm leading-relaxed">
                {audiovisualesData.incorporacion.descripcion}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ¿Qué obras comprende? */}
      <div className="px-4 md:px-6 lg:px-10 mb-16 md:mb-20">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h3 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              ¿Qué obras comprende?
            </h3>
            <p className="text-foreground-500 text-sm max-w-xl mx-auto">
              Las interpretaciones musicales en los siguientes formatos audiovisuales están protegidas.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {audiovisualesData.formatos.map((formato) => (
              <div
                key={formato.nombre}
                className="group bg-white border border-secondary-200/60 rounded-2xl p-5 md:p-6 transition-all duration-300 hover:-translate-y-1 hover:border-accent-200/50"
              >
                <div className="w-10 h-10 rounded-xl bg-accent-50 flex items-center justify-center mb-3 group-hover:bg-accent-100 transition-colors duration-300">
                  <i className={`${formato.icono} text-accent-600 text-lg`} />
                </div>
                <h5 className="font-heading text-base font-bold text-foreground-900 mb-1.5">
                  {formato.nombre}
                </h5>
                <p className="text-foreground-500 text-xs leading-relaxed">
                  {formato.descripcion}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Infografía: ¿Cómo funciona la gestión? */}
      <div className="px-4 md:px-6 lg:px-10 mb-16 md:mb-20">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block text-xs font-semibold uppercase tracking-widest text-accent-600 mb-2">
              Proceso
            </span>
            <h3 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
              ¿Cómo funciona la gestión?
            </h3>
          </div>

          {/* Desktop */}
          <div className="hidden lg:block">
            <div className="relative">
              <div className="absolute top-12 left-[8%] right-[8%] h-0.5 bg-gradient-to-r from-accent-200 via-accent-400 to-accent-200" />
              <div className="grid grid-cols-7 gap-3 relative">
                {audiovisualesData.infografia.map((paso) => (
                  <div key={paso.paso} className="flex flex-col items-center text-center group">
                    <div className="relative z-10 w-10 h-10 rounded-full bg-accent-500 text-white flex items-center justify-center text-xs font-bold mb-4 shadow-[0_0_20px_rgba(var(--accent-500),0.3)] group-hover:scale-110 transition-transform duration-300">
                      {paso.paso}
                    </div>
                    <div className="w-9 h-9 rounded-lg bg-accent-50 flex items-center justify-center mb-2">
                      <i className={`${paso.icono} text-accent-600 text-base`} />
                    </div>
                    <p className="text-foreground-900 text-xs font-bold mb-1 leading-tight whitespace-nowrap">
                      {paso.etiqueta}
                    </p>
                    <p className="text-foreground-500 text-[10px] leading-snug">
                      {paso.descripcion}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Mobile */}
          <div className="lg:hidden space-y-4">
            {audiovisualesData.infografia.map((paso, idx) => (
              <div key={paso.paso} className="flex gap-4 items-start">
                <div className="flex flex-col items-center">
                  <div className="w-9 h-9 rounded-full bg-accent-500 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                    {paso.paso}
                  </div>
                  {idx < audiovisualesData.infografia.length - 1 && (
                    <div className="w-0.5 h-8 bg-accent-200 mt-1" />
                  )}
                </div>
                <div className="pb-4">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-7 h-7 rounded-lg bg-accent-50 flex items-center justify-center">
                      <i className={`${paso.icono} text-accent-600 text-sm`} />
                    </div>
                    <p className="text-foreground-900 text-sm font-bold">{paso.etiqueta}</p>
                  </div>
                  <p className="text-foreground-500 text-xs">{paso.descripcion}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* SOY USUARIO / SOY ARTISTA */}
      <div className="px-4 md:px-6 lg:px-10">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
            {/* SOY USUARIO - Formulario + Contacto */}
            <div className="bg-white border border-secondary-200/60 rounded-3xl p-7 md:p-10">
              <div className="w-12 h-12 rounded-2xl bg-primary-100 flex items-center justify-center mb-5">
                <i className="ri-building-2-line text-primary-600 text-xl" />
              </div>
              <h3 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                {audiovisualesData.soyUsuario.titulo}
              </h3>
              <p className="text-foreground-600 text-sm leading-relaxed mb-6">
                {audiovisualesData.soyUsuario.descripcion}
              </p>

              {/* Form */}
              <form onSubmit={handleSubmit} data-readdy-form="" className="space-y-4">
                {/* Honeypot */}
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="ab-form-phone-alt"
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-foreground-700 mb-1.5">Nombre</label>
                    <input
                      type="text"
                      name="nombre"
                      required
                      className="w-full px-4 py-2.5 rounded-xl border border-secondary-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-300 transition-colors"
                      placeholder="Nombre completo"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-foreground-700 mb-1.5">Empresa</label>
                    <input
                      type="text"
                      name="empresa"
                      className="w-full px-4 py-2.5 rounded-xl border border-secondary-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-300 transition-colors"
                      placeholder="Razón social"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-foreground-700 mb-1.5">RUC</label>
                    <input
                      type="text"
                      name="ruc"
                      className="w-full px-4 py-2.5 rounded-xl border border-secondary-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-300 transition-colors"
                      placeholder="Número de RUC"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-foreground-700 mb-1.5">Correo</label>
                    <input
                      type="email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 rounded-xl border border-secondary-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-300 transition-colors"
                      placeholder="correo@empresa.com"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-foreground-700 mb-1.5">Teléfono</label>
                  <input
                    type="tel"
                    name="telefono"
                    className="w-full px-4 py-2.5 rounded-xl border border-secondary-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-300 transition-colors"
                    placeholder="Número de contacto"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-foreground-700 mb-1.5">Actividad</label>
                    <select
                      name="actividad"
                      className="w-full px-4 py-2.5 rounded-xl border border-secondary-200 bg-background-50 text-sm text-foreground-900 outline-none focus:border-primary-300 transition-colors cursor-pointer"
                    >
                      <option value="">Seleccionar</option>
                      <option value="television">Televisión</option>
                      <option value="cine">Cine</option>
                      <option value="streaming">Streaming</option>
                      <option value="publicidad">Publicidad</option>
                      <option value="produccion">Producción audiovisual</option>
                      <option value="otro">Otro</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-foreground-700 mb-1.5">Tipo de establecimiento</label>
                    <select
                      name="tipo_establecimiento"
                      className="w-full px-4 py-2.5 rounded-xl border border-secondary-200 bg-background-50 text-sm text-foreground-900 outline-none focus:border-primary-300 transition-colors cursor-pointer"
                    >
                      <option value="">Seleccionar</option>
                      <option value="productora">Productora</option>
                      <option value="canal_tv">Canal de TV</option>
                      <option value="agencia">Agencia de publicidad</option>
                      <option value="plataforma">Plataforma digital</option>
                      <option value="otro">Otro</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-foreground-700 mb-1.5">Departamento</label>
                    <select
                      name="departamento"
                      className="w-full px-4 py-2.5 rounded-xl border border-secondary-200 bg-background-50 text-sm text-foreground-900 outline-none focus:border-primary-300 transition-colors cursor-pointer"
                    >
                      <option value="">Seleccionar</option>
                      <option value="lima">Lima</option>
                      <option value="arequipa">Arequipa</option>
                      <option value="cusco">Cusco</option>
                      <option value="trujillo">La Libertad</option>
                      <option value="piura">Piura</option>
                      <option value="lambayeque">Lambayeque</option>
                      <option value="junin">Junín</option>
                      <option value="otro">Otro</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-foreground-700 mb-1.5">Distrito</label>
                    <input
                      type="text"
                      name="distrito"
                      className="w-full px-4 py-2.5 rounded-xl border border-secondary-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-300 transition-colors"
                      placeholder="Distrito"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-foreground-700 mb-1.5">
                    Observaciones <span className="text-foreground-400 font-normal">(máx. 500 caracteres)</span>
                  </label>
                  <textarea
                    name="observaciones"
                    rows={3}
                    maxLength={500}
                    className="w-full px-4 py-2.5 rounded-xl border border-secondary-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-300 transition-colors resize-none"
                    placeholder="Describa brevemente su consulta o requerimiento"
                  />
                </div>

                <button
                  type="submit"
                  disabled={formStatus === 'loading'}
                  className="w-full px-6 py-3 rounded-full bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer disabled:opacity-60"
                >
                  {formStatus === 'loading' ? 'Enviando...' : 'Solicitar liquidación'}
                </button>

                {formStatus === 'success' && (
                  <p className="text-primary-600 text-sm text-center font-medium">
                    Solicitud enviada correctamente. Nos pondremos en contacto pronto.
                  </p>
                )}
                {formStatus === 'error' && (
                  <p className="text-red-600 text-sm text-center font-medium">{formError}</p>
                )}
              </form>

              {/* Area de Recaudación */}
              <div className="mt-8 pt-6 border-t border-secondary-200">
                <h4 className="font-heading text-lg font-bold text-foreground-900 mb-4">
                  {audiovisualesData.soyUsuario.areaRecaudacion.titulo}
                </h4>
                <div className="space-y-3">
                  <a href={`mailto:${audiovisualesData.soyUsuario.areaRecaudacion.email}`} className="flex items-center gap-3 text-sm text-foreground-600 hover:text-primary-600 transition-colors">
                    <div className="w-8 h-8 rounded-lg bg-primary-50 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-primary-500" />
                    </div>
                    {audiovisualesData.soyUsuario.areaRecaudacion.email}
                  </a>
                  <div className="flex items-center gap-3 text-sm text-foreground-600">
                    <div className="w-8 h-8 rounded-lg bg-primary-50 flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-primary-500" />
                    </div>
                    <span>{audiovisualesData.soyUsuario.areaRecaudacion.telefono1} / {audiovisualesData.soyUsuario.areaRecaudacion.telefono2}</span>
                  </div>
                  <a href={`https://wa.me/${audiovisualesData.soyUsuario.areaRecaudacion.whatsapp.replace(/[\+\s]/g, '')}`} target="_blank" rel="noopener noreferrer nofollow" className="flex items-center gap-3 text-sm text-foreground-600 hover:text-primary-600 transition-colors">
                    <div className="w-8 h-8 rounded-lg bg-primary-50 flex items-center justify-center flex-shrink-0">
                      <i className="ri-whatsapp-line text-primary-500" />
                    </div>
                    {audiovisualesData.soyUsuario.areaRecaudacion.whatsapp}
                  </a>
                  <div className="flex items-center gap-3 text-sm text-foreground-600">
                    <div className="w-8 h-8 rounded-lg bg-primary-50 flex items-center justify-center flex-shrink-0">
                      <i className="ri-time-line text-primary-500" />
                    </div>
                    <span>{audiovisualesData.soyUsuario.areaRecaudacion.horario}</span>
                  </div>
                </div>
                <a
                  href={`https://wa.me/${audiovisualesData.soyUsuario.areaRecaudacion.whatsapp.replace(/[\+\s]/g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-100 text-primary-700 text-sm font-semibold hover:bg-primary-200 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-chat-3-line" />
                  <span>Hablar con un asesor</span>
                </a>
              </div>
            </div>

            {/* SOY ARTISTA */}
            <div className="bg-foreground-950 rounded-3xl p-7 md:p-10 text-white relative overflow-hidden">
              <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-white/[0.03]" />
              <div className="absolute -bottom-8 -left-8 w-28 h-28 rounded-full bg-white/[0.02]" />

              <div className="relative z-10">
                <div className="w-12 h-12 rounded-2xl bg-white/10 flex items-center justify-center mb-5">
                  <i className="ri-user-star-line text-white text-xl" />
                </div>
                <h3 className="font-heading text-2xl md:text-3xl font-bold mb-2">
                  Soy Artista
                </h3>
                <p className="text-white/60 text-sm leading-relaxed mb-3">
                  {audiovisualesData.soyArtista.titulo}
                </p>

                <p className="text-white/60 text-xs leading-relaxed mb-5">
                  {audiovisualesData.soyArtista.descripcionAfiliate}
                </p>

                <Link
                  to="/contacto"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-all duration-300 whitespace-nowrap mb-6 cursor-pointer"
                >
                  <span>Afíliate a SONIEM</span>
                  <i className="ri-arrow-right-line" />
                </Link>

                <div className="border-t border-white/10 pt-5">
                  <p className="text-white/60 text-xs leading-relaxed mb-3">
                    {audiovisualesData.soyArtista.descripcionRegistro}
                  </p>
                  <Link
                    to="/intranet"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white/10 text-white font-semibold text-sm border border-white/20 hover:bg-white/20 transition-all duration-300 whitespace-nowrap cursor-pointer"
                  >
                    <span>Registrar Producciones</span>
                    <i className="ri-arrow-right-line" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}